export interface CardPayload {
  type:
    | 'protocol_list'
    | 'project_list'
    | 'device_list'
    | 'device_status'
    | 'device_detail'
    | 'device_form'
    | 'docking_logs'
    | 'settlement_list'
    | 'yingshi_status'
    | 'actions_list'
    | 'form_schema'
    | 'confirm_write';
  title: string;
  data: unknown;
  links?: Array<{ label: string; path: string }>;
  actions?: Array<{ label: string; handler: string }>;
}

export interface ChatMessageItem {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  cards?: CardPayload[];
  toolStatus?: string;
  loading?: boolean;
  error?: boolean;
}

const AGENT_BASE_URL = process.env.REACT_APP_AGENT_URL || '/agent';

function getAuthHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const token = localStorage.getItem('token');
  if (token) {
    return { 'Authorization': `Bearer ${token}`, ...extra };
  }
  return extra;
}

export interface AgentChatCallbacks {
  onSession?: (sessionId: string) => void;
  onToolStatus?: (label: string) => void;
  onContent?: (content: string) => void;
  onCard?: (card: CardPayload) => void;
  onError?: (message: string) => void;
  onAuthError?: () => void; // 新增：处理认证失败回调
  onDone?: () => void;
}

export async function sendAgentMessage(
  message: string,
  sessionId: string | null,
  callbacks: AgentChatCallbacks
): Promise<void> {
  const token = localStorage.getItem('token');
  if (!token) {
    callbacks.onError?.('请先登录');
    return;
  }

  // 优先使用 localStorage 中持久化的 session
  const savedSessionId = localStorage.getItem('agent_session_id');
  const effectiveSessionId = sessionId || savedSessionId || null;

  const response = await fetch(`${AGENT_BASE_URL}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ message, sessionId: effectiveSessionId, stream: true }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ msg: response.statusText }));

    if (response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('agent_session_id');
      callbacks.onAuthError?.();
      return;
    }

    // 如果 sessionId 无效导致 500/404，清除本地缓存并重试（不带旧 sessionId）
    if ((response.status === 500 || response.status === 404) && effectiveSessionId) {
      localStorage.removeItem('agent_session_id');
      return sendAgentMessage(message, null, callbacks);
    }

    callbacks.onError?.(err.msg || '请求失败');
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    callbacks.onError?.('无法读取响应流');
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7).trim();
      } else if (line.startsWith('data: ') && currentEvent) {
        try {
          const data = JSON.parse(line.slice(6));
          switch (currentEvent) {
            case 'session':
              if (data.sessionId) {
                localStorage.setItem('agent_session_id', data.sessionId);
                callbacks.onSession?.(data.sessionId);
              }
              break;
            case 'tool_status':
              callbacks.onToolStatus?.(data.label || data.tool);
              break;
            case 'content':
              callbacks.onContent?.(data);
              break;
            case 'card':
              callbacks.onCard?.(data);
              break;
            case 'error':
              callbacks.onError?.(data.message || '未知错误');
              break;
            case 'done':
              callbacks.onDone?.();
              break;
            default:
              break;
          }
        } catch {
          // ignore parse errors
        }
        currentEvent = '';
      }
    }
  }

  callbacks.onDone?.();
}

export async function clearAgentSession(sessionId: string): Promise<boolean> {
  const token = localStorage.getItem('token');
  if (!token) return false;

  const res = await fetch(`${AGENT_BASE_URL}/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
  });
  if (res.ok) {
    localStorage.removeItem('agent_session_id');
  }
  return res.ok;
}
